import { css } from '@emotion/react';
import { t } from 'i18n';
import { ChangeEvent, ReactElement, useEffect, useRef, useState } from 'react';

import { theme } from '../theme';

interface SortSelectProps {
  sortOptions: { label: string; value: string }[];
  onSortChange: (option: string) => void;
}

export const styles = {
  container: css`
    display: flex;
    align-items: center;
    color: ${theme.colors.white};
    font-size: ${theme.fontSize.xs};
  `,
  dropdown: css`
    padding: ${theme.spacing.xs};
    padding-right: ${theme.spacing.lg};
    font-size: ${theme.fontSize.xs};
    background-color: transparent;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" role="img"><path d="M12.0001 15.0815C11.8508 15.0815 11.7079 15.0557 11.5713 15.004C11.4347 14.9524 11.3104 14.8706 11.1985 14.7586L6.60445 10.1646C6.39321 9.95339 6.2876 9.68817 6.2876 9.36897C6.2876 9.04977 6.39321 8.78455 6.60445 8.57332C6.81568 8.36209 7.0809 8.25647 7.4001 8.25647C7.7193 8.25647 7.98451 8.36209 8.19575 8.57332L12.0001 12.3777L15.8044 8.57332C16.0157 8.36209 16.2809 8.25647 16.6001 8.25647C16.9193 8.25647 17.1845 8.36209 17.3957 8.57332C17.607 8.78455 17.7126 9.04977 17.7126 9.36897C17.7126 9.68817 17.607 9.95339 17.3957 10.1646L12.8017 14.7586C12.6898 14.8706 12.5655 14.9524 12.4289 15.004C12.2923 15.0557 12.1494 15.0815 12.0001 15.0815Z"></path></svg>');
    background-position: right center;
    border: none;
    cursor: pointer;
    color: ${theme.colors.white};
    text-decoration: underline;

    > option {
      color: ${theme.colors.black};
    }
  `,
  widthHelper: css`
    padding: ${theme.spacing.xs};
    padding-right: ${theme.spacing.lg};
    font-size: ${theme.fontSize.xs};
    position: absolute;
    top: -100px;
    right: -1000px;
    opacity: 0%;
  `,
  alignText: css`
    display: flex;
    text-align: right;
  `,
};

export function SortSelect({ sortOptions, onSortChange }: SortSelectProps): ReactElement {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const selectRef = useRef<null | HTMLSelectElement>(null);
  const widthHelperRef = useRef<null | HTMLSelectElement>(null);

  useEffect(() => {
    const widthHelperElement = widthHelperRef.current;
    if (widthHelperElement) {
      widthHelperElement.innerText = sortOptions[0].label;
    }
  }, []);

  const handleWidth = (e: ChangeEvent<HTMLSelectElement>) => {
    const widthHelperElement = widthHelperRef.current || ({} as HTMLSelectElement);
    widthHelperElement.innerText = e.target.querySelector('option:checked')?.textContent ?? sortOptions[0].label;

    const selectElement = selectRef.current || ({} as HTMLSelectElement);
    selectElement.style.width = `${widthHelperElement.offsetWidth}px`;
  };

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(e.target.value);
    onSortChange(e.target.value);
    handleWidth(e);
  };

  return (
    <>
      <span className="widthHelper" css={styles.widthHelper} aria-hidden="true" ref={widthHelperRef} />
      <div css={styles.container}>
        <span css={styles.alignText}>
          <label>{t('sortBy')}:</label>
        </span>
        <div>
          <select value={selectedOption} onChange={handleSelect} css={styles.dropdown} ref={selectRef}>
            {sortOptions.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
}
